import React, { useEffect, useMemo, useRef, useState } from "react";
import { Columns3 } from "lucide-react";
import {
  GlobalButton,
  GlobalDateField,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import { CALL_LOG_COLUMN_DEFS } from "../../constants/callLogColumns";
import { formatCallDateDisplay, padTimeForApi, toCallLogCalendarDateKey } from "../../utils/callLogDates";

const PICKER_DEFS = CALL_LOG_COLUMN_DEFS.filter((c) => c.id !== "actions");

const INITIAL_VISIBILITY = Object.fromEntries(
  PICKER_DEFS.map((c) => [c.id, !!c.defaultVisible])
);

const TABLE_FILTER_COLUMNS = PICKER_DEFS.map((c) => ({ value: c.id, label: c.label }));

const getLocalDateString = () => {
  const now = new Date();
  const tzOffset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffset).toISOString().split("T")[0];
};

function getActiveColumnFilterValue(column, filters) {
  if (!column) return "";
  switch (column) {
    case "date":
      return String(filters.exact_call_date ?? "");
    case "time": {
      const raw = filters.call_time;
      if (raw == null || raw === "") return "";
      return padTimeForApi(raw);
    }
    case "caller_name":
      return String(filters.caller_name ?? "");
    case "phone":
      return String(filters.phone ?? "");
    case "call_type":
      return filters.call_type != null && filters.call_type !== ""
        ? String(filters.call_type)
        : "";
    case "purpose":
      return filters.purpose_id != null && filters.purpose_id !== ""
        ? String(filters.purpose_id)
        : "";
    case "status":
      return filters.is_call_completed != null && filters.is_call_completed !== ""
        ? String(filters.is_call_completed)
        : "";
    case "action":
      return String(filters.action_taken_contains ?? "");
    case "handled_by":
      return String(filters.contact_person_name ?? "");
    case "follow_up":
      return String(filters.exact_follow_up_date ?? "");
    case "duration":
      return filters.call_duration != null && filters.call_duration !== ""
        ? String(filters.call_duration)
        : "";
    case "remarks":
      return String(filters.remarks_contains ?? "");
    default:
      return "";
  }
}

function uniqSortedOptions(opts) {
  const seen = new Set();
  const out = [];
  for (const o of opts) {
    const k = String(o.value);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ value: k, label: o.label });
  }
  out.sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" }));
  return out;
}

const OfficeCallLogFilters = ({
  filters,
  onFilterChange,
  masterData,
  logs = [],
  catalogLogs = [],
  onColumnVisibilityChange,
  onExportCsv,
}) => {
  const [column, setColumn] = useState("");
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(INITIAL_VISIBILITY);
  const pickerRef = useRef(null);

  const sourceLogs = useMemo(() => {
    const map = new Map();
    for (const row of catalogLogs || []) {
      if (row?.id != null) map.set(row.id, row);
    }
    for (const row of logs || []) {
      if (row?.id != null) map.set(row.id, row);
    }
    const merged = Array.from(map.values());
    if (merged.length > 0) return merged;
    return [...(catalogLogs || []), ...(logs || [])];
  }, [catalogLogs, logs]);

  useEffect(() => {
    setColumnVisibility((prev) => {
      const next = { ...INITIAL_VISIBILITY };
      for (const c of PICKER_DEFS) {
        if (Object.prototype.hasOwnProperty.call(prev, c.id)) {
          next[c.id] = prev[c.id];
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof onColumnVisibilityChange === "function") {
      onColumnVisibilityChange(columnVisibility);
    }
  }, [columnVisibility, onColumnVisibilityChange]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!pickerRef.current?.contains(event.target)) setColumnPickerOpen(false);
    };
    if (columnPickerOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [columnPickerOpen]);

  const handleDateChange = (name) => (nextValue) => {
    onFilterChange((prev) => ({ ...prev, [name]: nextValue || "", page: 1 }));
  };

  const applyColumnValueFilter = (nextColumn, nextValue) => {
    const raw = nextValue != null ? String(nextValue).trim() : "";
    const timeNorm = nextColumn === "time" && raw ? padTimeForApi(raw) : "";
    onFilterChange((prev) => ({
      ...prev,
      search: "",
      caller_name: nextColumn === "caller_name" ? raw : "",
      phone: nextColumn === "phone" ? raw : "",
      call_type: nextColumn === "call_type" ? raw : "",
      purpose_id: nextColumn === "purpose" ? raw : "",
      is_call_completed: nextColumn === "status" ? raw : "",
      call_time: nextColumn === "time" ? timeNorm : "",
      exact_call_date: nextColumn === "date" ? raw : "",
      exact_follow_up_date: nextColumn === "follow_up" ? raw : "",
      remarks_contains: nextColumn === "remarks" ? raw : "",
      action_taken_contains: nextColumn === "action" ? raw : "",
      contact_person_name: nextColumn === "handled_by" ? raw : "",
      call_duration:
        nextColumn === "duration"
          ? raw === ""
            ? ""
            : (() => {
                const n = parseInt(raw, 10);
                return Number.isNaN(n) ? "" : String(n);
              })()
          : "",
      page: 1,
    }));
  };

  const activeValue = getActiveColumnFilterValue(column, filters);

  const valueOptions = useMemo(() => {
    if (!column) return [];
    let options = [];

    if (column === "call_type") {
      options = (masterData?.types || []).map((item) => ({
        value: String(item.id),
        label: item.type_name || item.label || String(item.id),
      }));
    } else if (column === "purpose") {
      options = (masterData?.purposes || []).map((item) => ({
        value: String(item.id),
        label: item.purpose_name || item.label || String(item.id),
      }));
    } else if (column === "status") {
      options = [
        { value: "1", label: "Completed" },
        { value: "0", label: "Needs follow-up" },
      ];
    } else if (column === "caller_name") {
      const unique = [...new Set((sourceLogs || []).map((x) => x?.caller_name).filter(Boolean))];
      options = unique.map((text) => ({ value: String(text), label: String(text) }));
    } else if (column === "phone") {
      const unique = [...new Set((sourceLogs || []).map((x) => x?.contact_number).filter(Boolean))];
      options = unique.map((text) => ({ value: String(text), label: String(text) }));
    } else if (column === "date") {
      const unique = [
        ...new Set(
          (sourceLogs || [])
            .map((x) => toCallLogCalendarDateKey(x?.call_date))
            .filter(Boolean)
        ),
      ];
      options = unique.map((key) => ({
        value: key,
        label: formatCallDateDisplay(key),
      }));
    } else if (column === "time") {
      const rawTimes = (sourceLogs || []).map((x) => x?.call_time).filter(Boolean);
      const normalized = [...new Set(rawTimes.map((t) => padTimeForApi(t)).filter(Boolean))];
      options = normalized.map((t) => ({
        value: t,
        label: t.length >= 8 ? t.slice(0, 5) : t,
      }));
    } else if (column === "follow_up") {
      const unique = [
        ...new Set(
          (sourceLogs || [])
            .map((x) => toCallLogCalendarDateKey(x?.follow_up_date))
            .filter(Boolean)
        ),
      ];
      options = unique.map((key) => ({
        value: key,
        label: formatCallDateDisplay(key),
      }));
    } else if (column === "duration") {
      const unique = [
        ...new Set(
          (sourceLogs || [])
            .map((x) => x?.call_duration)
            .filter((v) => v != null && v !== "")
            .map((v) => String(parseInt(v, 10)))
            .filter((s) => s !== "" && !Number.isNaN(Number(s)))
        ),
      ];
      options = unique.map((mins) => ({
        value: mins,
        label: `${mins} min`,
      }));
    } else if (column === "action") {
      const texts = (sourceLogs || [])
        .map((x) => (x?.action_taken != null ? String(x.action_taken).trim() : ""))
        .filter(Boolean);
      const unique = [...new Set(texts)];
      options = unique.map((text) => ({
        value: text,
        label: text.length > 72 ? `${text.slice(0, 72)}…` : text,
      }));
    } else if (column === "handled_by") {
      const unique = [...new Set((sourceLogs || []).map((x) => x?.handled_by_name).filter(Boolean))];
      options = unique.map((text) => ({ value: String(text), label: String(text) }));
    } else if (column === "remarks") {
      const texts = (sourceLogs || []).map((x) => (x?.remarks != null ? String(x.remarks).trim() : "")).filter(Boolean);
      const unique = [...new Set(texts)];
      options = unique.map((text) => ({
        value: text,
        label: text.length > 72 ? `${text.slice(0, 72)}…` : text,
      }));
    }

    if (activeValue && !options.some((o) => String(o.value) === String(activeValue))) {
      let injectLabel = String(activeValue);
      if (column === "date" || column === "follow_up") {
        injectLabel = formatCallDateDisplay(activeValue);
      } else if (column === "duration") {
        injectLabel = `${activeValue} min`;
      } else if (column === "remarks" || column === "action") {
        injectLabel = activeValue.length > 72 ? `${activeValue.slice(0, 72)}…` : activeValue;
      }
      options = [{ value: String(activeValue), label: injectLabel }, ...options];
    }

    if (column === "purpose" || column === "call_type") {
      return uniqSortedOptions(options);
    }
    if (column === "status") {
      return options;
    }
    if (column === "date" || column === "time" || column === "follow_up") {
      return options.sort((a, b) => String(b.value).localeCompare(String(a.value)));
    }
    if (column === "duration") {
      return options.sort((a, b) => Number(b.value) - Number(a.value));
    }
    return uniqSortedOptions(options);
  }, [column, sourceLogs, masterData, activeValue]);

  const handleColumnChange = (nextColumn) => {
    const normalized = nextColumn || "";
    setColumn(normalized);
    applyColumnValueFilter(normalized, "");
  };

  const handleValueChange = (nextValue) => {
    const normalized = nextValue != null ? String(nextValue) : "";
    applyColumnValueFilter(column, normalized);
  };

  const handleReset = () => {
    setColumn("");
    onFilterChange((prev) => ({
      ...prev,
      search: "",
      caller_name: "",
      phone: "",
      call_type: "",
      purpose_id: "",
      is_call_completed: "",
      call_time: "",
      exact_call_date: "",
      exact_follow_up_date: "",
      remarks_contains: "",
      action_taken_contains: "",
      call_duration: "",
      contact_person_name: "",
      start_date: "",
      end_date: "",
      page: 1,
    }));
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.75rem",
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 170px", minWidth: "170px" }}>
          <GlobalDateField
            label="From Date"
            value={filters.start_date || ""}
            onChange={handleDateChange("start_date")}
            max={getLocalDateString()}
          />
        </div>
        <div style={{ flex: "1 1 170px", minWidth: "170px" }}>
          <GlobalDateField
            label="To Date"
            value={filters.end_date || ""}
            onChange={handleDateChange("end_date")}
            max={getLocalDateString()}
          />
        </div>
        <div style={{ flex: "1 1 170px", minWidth: "170px" }}>
          <GlobalSelectField
            label="Column"
            value={column}
            onChange={handleColumnChange}
            options={TABLE_FILTER_COLUMNS}
            placeholderOption="Select"
          />
        </div>
        <div style={{ flex: "1 1 170px", minWidth: "170px" }}>
          <GlobalSelectField
            label="Value"
            value={activeValue}
            onChange={handleValueChange}
            options={valueOptions}
            placeholderOption="All"
            disabled={!column}
          />
        </div>
        <div style={{ flex: "0 0 auto" }}>
          <GlobalButton type="button" size="sm" variant="ghost" onClick={handleReset}>
            Clear filter
          </GlobalButton>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.65rem",
          marginTop: "0.65rem",
          width: "100%",
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0.2rem 0" }}>
          <div className="vt-column-picker" ref={pickerRef}>
            <GlobalButton
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setColumnPickerOpen((open) => !open)}
              style={{ width: "fit-content", whiteSpace: "nowrap" }}
            >
              <Columns3 size={16} aria-hidden />
              <span style={{ marginLeft: "0.35rem" }}>Columns</span>
            </GlobalButton>
            {columnPickerOpen ? (
              <div
                className="vt-column-picker__panel vt-column-picker__panel--call-logs"
                role="group"
                aria-label="Choose visible columns"
              >
                <p className="vt-column-picker__title">Show columns</p>
                <ul className="vt-column-picker__list">
                  {PICKER_DEFS.map((c) => {
                    const enabled = columnVisibility[c.id] !== false;
                    return (
                      <li key={c.id}>
                        <label className="vt-column-picker__row">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() =>
                              setColumnVisibility((prev) => ({
                                ...INITIAL_VISIBILITY,
                                ...prev,
                                [c.id]: !enabled,
                              }))
                            }
                          />
                          <span>{c.label}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
        <GlobalButton
          type="button"
          size="sm"
          variant="ghost"
          onClick={onExportCsv}
          style={{ flexShrink: 0, width: "fit-content", whiteSpace: "nowrap" }}
        >
          Export CSV
        </GlobalButton>
      </div>
    </div>
  );
};

export default OfficeCallLogFilters;
